<template>
	<div
		v-if="tokenReady && isDesktop"
		class="main-page__desktop"
		ref="containerRef"
	>
		<div
			v-for="(block, index) in blocks"
			:key="index"
			class="main-page__panel"
			:style="{
					width: block.widthPx + 'px',
					minWidth: block.minWidth + 'px'
				}"
		>
			<div class="main-page__panel-content">
				<component :is="components[block.id]" />
			</div>

			<!-- Ресайзер справа -->
			<div
				v-if="index < blocks.length - 1"
				class="main-page__resizer main-page__resizer--right"
				@mousedown="startResize(index, $event)"
			></div>
		</div>
		<!-- Визуальный ресайзер -->
		<div
			v-if="isResizing"
			class="main-page__visual-resizer"
			:style="{
				left: visualResizerPosition + 'px',
				top: visualResizerTop + 'px',
				height: visualResizerHeight + 'px'
			}"
		>
			<div class="main-page__visual-resizer-line"></div>
			<div class="main-page__visual-resizer-handle"></div>
		</div>
	</div>
	<swiper
		v-else-if="tokenReady && (isMobile || isTablet)"
		@swiper="(s) => swiperInstance = s"
		:slides-per-view="1"
		:space-between="10"
		:breakpoints="{
			769: {
				slidesPerView: 'auto',
				spaceBetween: 10
			}
		}"
		:modules="[Pagination]"
		pagination
		class="main-page__mobile"
	>
		<swiper-slide class="main-page__slide main-page__slide--search">
			<search-panel v-if="tokenReady" />
		</swiper-slide>
		<swiper-slide class="main-page__slide main-page__slide--jobs">
			<jobs-panel v-if="tokenReady" />
		</swiper-slide>
		<swiper-slide class="main-page__slide main-page__slide--chat">
			<div class="main-page__chat-wrapper">
				<component :is="mainComponent" v-if="tokenReady" />
			</div>
		</swiper-slide>
		<swiper-slide class="main-page__slide main-page__slide--clients">
			<component :is="contactsComponent" v-if="tokenReady" />
		</swiper-slide>

		<template #pagination>
			<div class="swiper-pagination"></div>
		</template>
	</swiper>

	<div
		v-if="tokenReady && accessDenied"
		class="main-page__no-access"
	>
		<app-icon
			name="invalid"
			width="32px"
			color="#979797"
		/>
		<div class="main-page__no-access-label">
			{{ noAccessMessage }}
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	defineOptions,
	ref,
	watch,
	onMounted,
	onUnmounted,
	computed,
	markRaw,
	nextTick
} from "vue";
import {useRoute, useRouter} from "vue-router";
import {ChatModes, chatsStore} from "@/store/chats";
import {homePageStore, HomePageTypes} from "@/store/home-page";
import {projectPageStore} from "@/store/project-page";
import {clientPageStore} from "@/store/client-page";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";

import { useBreakpoints } from "@/composables/useBreakpoints";
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Pagination } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import ClientsPanel from "@/components/main/clients-panel/ClientsPanel.vue";
import ClientBlockPageContacts from "@/components/main/client-block-page/ClientBlockPageContacts.vue";
import ProjectBlockPageContacts from "@/components/main/project-block-page/ProjectBlockPageContacts.vue";
import ChatPanel from "@/components/main/chat-panel/ChatPanel.vue";
import SearchPanel from "@/components/main/search-panel/SearchPanel.vue";
import JobsPanel from "@/components/main/jobs-panel/JobsPanel.vue";
import ProjectBlockPage from "@/components/main/project-block-page/ProjectBlockPage.vue";
import ClientBlockPage from "@/components/main/client-block-page/ClientBlockPage.vue";

defineOptions({name: "MainPage"});

const route = useRoute();
const router = useRouter();
const chatStore = chatsStore();
const homeStore = homePageStore();
const projectStore = projectPageStore();
const clientStore = clientPageStore();
const { isTablet, isMobile, isDesktop } = useBreakpoints();

const accessDenied = computed(() => {
	if (route.query.page === 'project') return projectStore.accessDenied;
	if (route.query.page === 'client') return clientStore.accessDenied;
	return false;
});

const noAccessMessage = computed(() =>
	route.query.page === 'project'
		? 'У вас отсутствует доступ к странице проекта'
		: 'У вас отсутствует доступ к странице клиента'
);
const tokenReady = ref(false);
const swiperInstance = ref<SwiperType | null>(null)
const containerRef = ref(null)
const containerWidth = ref(0)
let resizeObserver: ResizeObserver | null = null

const blocks = ref([
	{ id: 'search', widthPx: 0, minWidth: 328, widthPercent: 0.25 },
	{ id: 'job', widthPx: 0, minWidth: 328, widthPercent: 0.25 },
	{ id: 'mainBlock', widthPx: 0, minWidth: 328, widthPercent: 0.25 },
	{ id: 'contacts', widthPx: 0, minWidth: 328, widthPercent: 0.25 },
])

// Выбор основного компонента
const mainComponent = computed(() => {
	const page = route.query.page

	if (page === 'project') {
		return markRaw(ProjectBlockPage)
	}

	if (page === 'client') {
		return markRaw(ClientBlockPage)
	}

	return markRaw(ChatPanel)
})

// Выбор компонента c контактами
const contactsComponent = computed(() => {
	const page = route.query.page

	if (page === 'project') {
		return markRaw(ProjectBlockPageContacts)
	}

	if (page === 'client') {
		return markRaw(ClientBlockPageContacts)
	}

	return markRaw(ClientsPanel)
})

const components = computed(() => ({
	search: markRaw(SearchPanel),
	job: markRaw(JobsPanel),
	mainBlock: mainComponent.value,
	contacts: contactsComponent.value
}))

const STORAGE_KEY = 'home-blocks-position_v1.2'

const isResizing = ref(false)
const currentBlockIndex = ref(0)
const startX = ref(0)
const startWidthsPercent = ref([])
const visualResizerPosition = ref(0)
const initialResizerPosition = ref(0)
const visualResizerTop = ref(0)
const visualResizerHeight = ref(0)

// Минимальные ширины в процентах
const minWidthsPercent = computed(() => {
	if (containerWidth.value <= 0) {
		return blocks.value.map(() => 0)
	}
	return blocks.value.map(block => block.minWidth / containerWidth.value)
})

const loadBlocksFromStorage = () => {
	try {
		const saved = localStorage.getItem(STORAGE_KEY)
		if (saved) {
			const parsed = JSON.parse(saved)
			// Загружаем проценты вместо пикселей
			return parsed.map(block => ({
				widthPercent: block.widthPercent || block.percentage / 100,
				minWidth: block.minWidth
			}))
		}
	} catch (error) {
		console.warn('Не удалось загрузить размеры:', error)
	}
	return null
}

const saveBlocksToStorage = () => {
	try {
		const sizesToSave = blocks.value.map(block => ({
			widthPercent: block.widthPercent,
			minWidth: block.minWidth
		}))
		localStorage.setItem(STORAGE_KEY, JSON.stringify(sizesToSave))
	} catch (error) {
		console.warn('Не удалось сохранить размеры:', error)
	}
}

const updateContainerWidth = () => {
	if (containerRef.value) {
		containerWidth.value = containerRef.value.clientWidth
		updateBlockWidthsPx()
	}
}

const updateBlockWidthsPx = () => {
	if (containerWidth.value <= 0) {
		return
	}

	const totalMinWidth = blocks.value.reduce((sum, b) => sum + b.minWidth, 0)

	if (containerWidth.value < totalMinWidth) {
		// Режим скролла: все блоки на минимуме
		blocks.value.forEach(b => {
			b.widthPx = b.minWidth
		})
		return
	}

	// Нормальный режим: проценты с итеративным перераспределением
	const widths = blocks.value.map(b => b.widthPercent * containerWidth.value)
	const locked = new Array(widths.length).fill(false)

	// Поочередно фиксируем блоки на минимуме и перераспределяем остаток
	let changed = true
	while (changed) {
		changed = false
		let lockedTotal = 0
		let flexiblePercentTotal = 0

		for (let i = 0; i < widths.length; i++) {
			if (locked[i]) {
				lockedTotal += blocks.value[i].minWidth
			} else {
				flexiblePercentTotal += blocks.value[i].widthPercent
			}
		}

		const availableWidth = containerWidth.value - lockedTotal

		for (let i = 0; i < widths.length; i++) {
			if (locked[i]) continue

			const proportionalWidth = flexiblePercentTotal > 0
				? (blocks.value[i].widthPercent / flexiblePercentTotal) * availableWidth
				: blocks.value[i].minWidth

			if (proportionalWidth < blocks.value[i].minWidth) {
				widths[i] = blocks.value[i].minWidth
				locked[i] = true
				changed = true
			} else {
				widths[i] = proportionalWidth
			}
		}
	}

	blocks.value.forEach((b, i) => {
		b.widthPx = widths[i]
	})
}

const calculateInitialWidths = () => {
	updateContainerWidth()

	const savedSizes = loadBlocksFromStorage()
	if (savedSizes && savedSizes.length === blocks.value.length) {
		// Используем сохраненные проценты
		blocks.value.forEach((block, index) => {
			block.widthPercent = savedSizes[index].widthPercent
		})
	} else {
		// Используем начальные проценты
		blocks.value.forEach((block, index) => {
			const percentages = [0.2, 0.2, 0.4, 0.2]
			block.widthPercent = percentages[index]
		})
	}

	updateBlockWidthsPx()
}

const startResize = (index, event) => {
	event.preventDefault()

	const containerRect = containerRef.value.getBoundingClientRect()
	visualResizerTop.value = containerRect.top
	visualResizerHeight.value = containerRect.height

	isResizing.value = true
	currentBlockIndex.value = index

	// Вычисляем позицию ресайзера в пикселях
	let resizerX = 0
	for (let i = 0; i <= index; i++) {
		resizerX += blocks.value[i].widthPx
	}

	startX.value = event.clientX
	// Сохраняем начальные проценты
	startWidthsPercent.value = blocks.value.map(block => block.widthPercent)
	initialResizerPosition.value = resizerX
	visualResizerPosition.value = resizerX

	document.addEventListener('mousemove', handleResizeMove)
	document.addEventListener('mouseup', stopResize)
	document.body.style.userSelect = 'none'
	document.body.style.cursor = 'col-resize'
}

let resizeRafId: number | null = null
let lastMoveEvent: MouseEvent | null = null

const cancelResizeRaf = () => {
	if (resizeRafId !== null) {
		cancelAnimationFrame(resizeRafId)
		resizeRafId = null
	}
}

const applyResizeMove = (event) => {
	if (!isResizing.value) {
		return
	}

	const deltaX = event.clientX - startX.value
	const targetPosition = initialResizerPosition.value + deltaX

	const { minPosition, maxPosition } = calculatePositionLimits()

	if (targetPosition >= minPosition && targetPosition <= maxPosition) {
		visualResizerPosition.value = targetPosition
	} else if (targetPosition < minPosition) {
		visualResizerPosition.value = minPosition
	} else if (targetPosition > maxPosition) {
		visualResizerPosition.value = maxPosition
	}
}

const handleResizeMove = (event) => {
	lastMoveEvent = event
	if (resizeRafId !== null) {
		return
	}
	resizeRafId = requestAnimationFrame(() => {
		resizeRafId = null
		if (lastMoveEvent) {
			applyResizeMove(lastMoveEvent)
		}
	})
}

const calculatePositionLimits = () => {
	const resizerIndex = currentBlockIndex.value

	// Минимальная позиция (в пикселях)
	let minPosition = initialResizerPosition.value
	for (let i = resizerIndex; i >= 0; i--) {
		const availableShrink = (startWidthsPercent.value[i] - minWidthsPercent.value[i]) * containerWidth.value
		minPosition -= availableShrink
	}

	// Максимальная позиция (в пикселях)
	let maxPosition = initialResizerPosition.value
	for (let i = resizerIndex + 1; i < blocks.value.length; i++) {
		const availableShrink = (startWidthsPercent.value[i] - minWidthsPercent.value[i]) * containerWidth.value
		maxPosition += availableShrink
	}

	return { minPosition, maxPosition }
}

const stopResize = () => {
	if (!isResizing.value) {
		return
	}

	if (resizeRafId !== null) {
		cancelResizeRaf()
		if (lastMoveEvent) {
			applyResizeMove(lastMoveEvent)
		}
	}

	const finalDeltaX = visualResizerPosition.value - initialResizerPosition.value
	const deltaPercent = finalDeltaX / containerWidth.value

	applyResize(deltaPercent)
	saveBlocksToStorage()

	isResizing.value = false
	document.removeEventListener('mousemove', handleResizeMove)
	document.removeEventListener('mouseup', stopResize)
	document.body.style.userSelect = ''
	document.body.style.cursor = ''
}

const applyResize = (deltaPercent) => {
	const resizerIndex = currentBlockIndex.value
	let remainingDelta = deltaPercent

	if (remainingDelta > 0) {
		// Двигаем вправо - расширяем левый блок, сужаем правые
		const nextBlock = blocks.value[resizerIndex + 1]
		const availableFromNext = startWidthsPercent.value[resizerIndex + 1] - minWidthsPercent.value[resizerIndex + 1]

		if (remainingDelta <= availableFromNext) {
			blocks.value[resizerIndex].widthPercent = startWidthsPercent.value[resizerIndex] + remainingDelta
			nextBlock.widthPercent = startWidthsPercent.value[resizerIndex + 1] - remainingDelta
		} else {
			blocks.value[resizerIndex].widthPercent = startWidthsPercent.value[resizerIndex] + availableFromNext
			nextBlock.widthPercent = minWidthsPercent.value[resizerIndex + 1]
			remainingDelta -= availableFromNext

			for (let i = resizerIndex + 2; i < blocks.value.length && remainingDelta > 0; i++) {
				const block = blocks.value[i]
				const available = startWidthsPercent.value[i] - minWidthsPercent.value[i]
				const taken = Math.min(available, remainingDelta)

				blocks.value[resizerIndex].widthPercent += taken
				block.widthPercent = startWidthsPercent.value[i] - taken
				remainingDelta -= taken
			}
		}
	} else if (remainingDelta < 0) {
		// Двигаем влево - сужаем левый блок, расширяем правые
		remainingDelta = Math.abs(remainingDelta)
		const currentBlock = blocks.value[resizerIndex]
		const availableFromCurrent = startWidthsPercent.value[resizerIndex] - minWidthsPercent.value[resizerIndex]

		if (remainingDelta <= availableFromCurrent) {
			currentBlock.widthPercent = startWidthsPercent.value[resizerIndex] - remainingDelta
			blocks.value[resizerIndex + 1].widthPercent = startWidthsPercent.value[resizerIndex + 1] + remainingDelta
		} else {
			currentBlock.widthPercent = minWidthsPercent.value[resizerIndex]
			blocks.value[resizerIndex + 1].widthPercent = startWidthsPercent.value[resizerIndex + 1] + availableFromCurrent
			remainingDelta -= availableFromCurrent

			for (let i = resizerIndex - 1; i >= 0 && remainingDelta > 0; i--) {
				const block = blocks.value[i]
				const available = startWidthsPercent.value[i] - minWidthsPercent.value[i]
				const taken = Math.min(available, remainingDelta)

				block.widthPercent = startWidthsPercent.value[i] - taken
				blocks.value[resizerIndex + 1].widthPercent += taken
				remainingDelta -= taken
			}
		}
	}

	// Корректируем сумму процентов (должна быть равна 1)
	const totalPercent = blocks.value.reduce((sum, block) => sum + block.widthPercent, 0)
	if (Math.abs(totalPercent - 1) > 0.001) {
		const correction = (1 - totalPercent) / blocks.value.length
		blocks.value.forEach(block => {
			block.widthPercent += correction
		})
	}

	updateBlockWidthsPx()
}

const handleResizeWindow = () => {
	updateContainerWidth()
}

watch(containerWidth, () => {
	updateBlockWidthsPx()
})

watch(() => ({ page: route.query.page, id: route.query.id }), ({ page }) => {
	projectStore.accessDenied = false
	clientStore.accessDenied = false
	if ((page === 'client' || page === 'project') && swiperInstance.value) {
		swiperInstance.value.slideTo(2)
	}
}, { deep: true })

onMounted(async () => {
	tokenReady.value = true;

	if (!route.query.page || !['chat', 'client', 'project'].includes(route.query.page as string)) {
		homeStore.setCurrentPage('chat')
		chatStore.setMode('list');
		await router.push({
			path: '/',
			query: {
				page: 'chat',
				mode: 'list'
			}
		});
	} else {
		if (route.query.page === 'chat') {
			homeStore.setCurrentPage('chat')
			chatStore.setMode(route.query.mode as ChatModes);
		} else {
			homeStore.setCurrentPage(route.query.page as HomePageTypes);
		}
	}
	await nextTick()

	calculateInitialWidths()
	window.addEventListener('resize', handleResizeWindow)

	// Добавляем ResizeObserver для отслеживания изменений размера контейнера
	resizeObserver = new ResizeObserver(() => {
		updateContainerWidth()
	})
	if (containerRef.value) {
		resizeObserver.observe(containerRef.value)
	}
});

onUnmounted(() => {
	cancelResizeRaf()
	document.removeEventListener('mousemove', handleResizeMove)
	document.removeEventListener('mouseup', stopResize)
	window.removeEventListener('resize', handleResizeWindow)
	resizeObserver?.disconnect()
	resizeObserver = null
})
</script>

<style lang="scss" scoped>
.main-page {
	&__desktop {
		display: flex;
		position: relative;
		width: 100%;
		height: 100%;
		overflow-x: auto;
	}

	&__panel {
		position: relative;
		height: 100%;
		width: 100%;
		background: transparent;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	&__panel-content {
		display: flex;
		flex-direction: column;
		max-height: 100%;
		height: 100%;
		width: 100%;
		padding: 4px;
		box-sizing: border-box;
	}

	&__resizer {
		position: absolute;
		top: 0;
		width: 8px;
		height: 100%;
		background: transparent;
		cursor: col-resize;
		z-index: 10;
		opacity: 0;

		&:hover {
			opacity: 1;
			background: rgba(57, 103, 66, 0.2);
		}

		&::after {
			content: '';
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			width: 4px;
			height: 60px;
			background: rgba(57, 103, 66, 0.4);
			border-radius: 4px;
		}

		&--right {
			right: -4px;
		}
	}

	&__visual-resizer {
		position: fixed;
		width: 8px;
		height: 100%;
		background: transparent;
		cursor: col-resize;
		z-index: 1000;
	}

	&__visual-resizer-line {
		width: 2px;
		height: 100%;
		background: rgba(57, 103, 66, 0.2);
	}

	&__visual-resizer-handle {
		position: absolute;
		width: 12px;
		height: 60px;
		background: #007bff;
		border-radius: 6px;
		border: 2px solid white;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	}

	&__mobile {
		display: flex;
		flex-direction: row;
		height: 100%;
		max-height: 100%;
		padding: 5px;

		&::v-deep(.q-field--outlined .q-field__control:hover:before) {
			border-color: #499259;
		}
	}

	&__slide {
		height: 100%;
		display: flex;
		justify-content: center;
		align-items: center;
		border-radius: 8px;
		color: white;
		user-select: none;
		box-sizing: border-box;

		@media (max-width: 767px) {
			width: 100% !important;
		}

		@media (min-width: 768px) {
			&--search {
				width: calc(20% - 7px);
			}

			&--jobs {
				min-width: calc(20% - 7px);
				max-width: calc(20% - 7px);
			}

			&--chat {
				width: calc(40% - 7px);
			}

			&--clients {
				width: calc(20% - 7px);
			}
		}
	}

	&__chat-wrapper {
		display: flex;
		flex-direction: column;
		height: 100%;
		min-height: 0;
		width: 100%;
	}

	&__no-access {
		position: absolute;
		inset: 0;
		z-index: 1001;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 8px;
		background: #F2F2F2;
	}

	&__no-access-label {
		color: #979797;
		text-align: center;
		font-size: 16px;
		font-weight: 600;
		line-height: 20px;
	}
}
</style>
